import React, { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import i18n from "@core/i18n";
import CloseButton from "@core/components/ui/CloseButton";
import { Button } from "@core/components/ui/button";
import { useNavigate } from "react-router-dom";
import PollActions from "@core/components/polls/PollActions";
import ResultsLayout from "@core/components/polls/ResultsLayout";
import {
  ResultsEmpty,
  ResultsLoading,
} from "@core/components/polls/ResultsStates";
import { ResultsAccessDenied } from "@core/components/polls/ResultsAccessDenied";
import PollAnalyticsPanel from "@core/components/polls/PollAnalyticsPanel";
import { NPSResults } from "./NPSResults";
// import DateResultsView from "@core/components/polls/DateResultsView"; // MIGRÉ vers apps/date-polls/src/components/
import { useResultsAccess } from "@core/hooks/useResultsAccess";
import { usePollBySlug } from "@core/hooks/usePoll";
import { getRespondentId } from "@core/lib/pollStorage";
import { VoteService } from "@core/services/VoteService";
import {
  voteRowToFormResponse,
  aggregateFormResults,
} from "@core/lib/formResults";
import { getCurrentUserId, getDeviceId } from "@core/lib/guestIdentityService";
import { useAuth } from "@core/contexts/AuthContext";
import type {
  FormQuestionShape,
  FormQuestionOption,
  FormResponse,
} from "@core/lib/pollStorage";
import { getComparisonByPollId } from "@core/lib/simulation/SimulationComparison";
import { Target, TrendingUp, Clock, AlertCircle } from "lucide-react";
import { useToast } from "@core/hooks/use-toast";

interface Props {
  idOrSlug: string;
}

export default function FormPollResults({ idOrSlug }: Props) {
  const { t } = useTranslation("forms");
  const navigate = useNavigate();
  const { user } = useAuth(); // Récupérer l'utilisateur authentifié
  const { toast } = useToast();

  // Utiliser le hook unifié avec le slug
  const { data: poll, isLoading, error } = usePollBySlug(idOrSlug);

  const [total, setTotal] = useState(0);
  const [countsByQuestion, setCountsByQuestion] = useState<
    Record<string, Record<string, number>>
  >({});
  const [textAnswers, setTextAnswers] = useState<Record<string, string[]>>({});
  const [dateResults, setDateResults] = useState<
    Record<string, import("@core/lib/pollStorage").DateQuestionResults>
  >({});
  const [responses, setResponses] = useState<FormResponse[]>([]);
  const [expandedText, setExpandedText] = useState<Record<string, boolean>>({});
  const [showComparisonDetails, setShowComparisonDetails] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);

  useEffect(() => {
    if (!poll || poll.type !== "form") return;
    let cancelled = false;
    (async () => {
      try {
        const rows = await VoteService.listByPollIdAndType(poll.id, "form");
        if (cancelled) return;
        const formResponses = rows.map(voteRowToFormResponse);
        const questions = ((poll as any)?.questions ??
          (poll as any)?.poll_data?.questions ??
          (poll as any)?.poll_data_json?.questions ??
          []) as FormQuestionShape[];
        const res = aggregateFormResults(poll.id, formResponses, questions);
        setCountsByQuestion(res.countsByQuestion);
        setTextAnswers(res.textAnswers);
        setDateResults(res.dateResults || {});
        setTotal(res.totalResponses);
        setResponses(formResponses);
      } catch {
        if (cancelled) return;
        setCountsByQuestion({});
        setTextAnswers({});
        setDateResults({});
        setTotal(0);
        setResponses([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [poll]);

  const questions = useMemo(
    () =>
      ((poll as any)?.questions ??
        (poll as any)?.poll_data?.questions ??
        (poll as any)?.poll_data_json?.questions ??
        []) as FormQuestionShape[],
    [poll],
  );

  // Adapter le poll pour les composants qui attendent l'ancien format
  const adaptedPoll = useMemo(() => {
    if (!poll) return null;
    return {
      ...poll,
      creator_id: poll.creator_id || "unknown",
      questions:
        (poll as any).questions ||
        (poll as any).poll_data?.questions ||
        (poll as any).poll_data_json?.questions ||
        [],
      settings: {
        showTimeSlots: false,
      },
    };
  }, [poll]);

  // Vérifier si l'utilisateur a voté — depuis les responses chargées via
  // VoteService (Supabase). On match sur deviceId / stableFingerprint au
  // lieu de l'ancien checkIfUserHasVoted (qui lisait localStorage).
  const hasVoted = useMemo(() => {
    if (!poll || responses.length === 0) return false;
    const deviceId = getDeviceId();
    return responses.some(
      (r) => r.deviceId === deviceId || getRespondentId(r).includes(deviceId),
    );
  }, [poll, responses]);

  // Vérifier l'accès aux résultats
  const accessStatus = useResultsAccess(adaptedPoll as any, hasVoted);

  const currentUserId = getCurrentUserId(user?.id);
  // Créateur authentifié (creator_id === user) OU créateur invité : le RPC
  // get_poll_by_slug ne révèle guest_fingerprint qu'au vrai créateur, donc sa
  // présence suffit à l'identifier (cf. useResultsAccess). Sans ça, un créateur
  // invité ne verrait pas ses actions (export, etc.).
  const isCreator =
    poll?.creator_id === currentUserId ||
    (!poll?.creator_id && !!(poll as any)?.guest_fingerprint);

  // Deduplicate responses by stable respondent id
  const uniqueResponses = useMemo(() => {
    const map = new Map<string, FormResponse>();
    for (const r of responses) {
      const id = getRespondentId(r);
      if (!map.has(id)) map.set(id, r);
    }
    return Array.from(map.values());
  }, [responses]);

  const computed = useMemo(() => {
    const map: Record<
      string,
      {
        kind: string;
        counts?: Record<string, number>;
        samples?: string[];
        count?: number;
      }
    > = {};
    for (const q of questions) {
      const qid: string = q.id;
      const kind: string = q.kind || q.type || "single";
      if (kind === "text" || kind === "long-text") {
        const samples = (textAnswers[qid] || []).slice(0, 10);
        map[qid] = { kind, count: (textAnswers[qid] || []).length, samples };
      } else {
        map[qid] = { kind, counts: countsByQuestion[qid] || {} };
      }
    }
    return map;
  }, [questions, countsByQuestion, textAnswers]);

  // Phase 3A prod-readiness : valeurs numériques (rating/nps) extraites une
  // seule fois par render au lieu d'un map+filter sur toutes les réponses pour
  // chaque question rating/nps. Le mesurage (P0) montre que c'est largement
  // suffisant à l'échelle réelle — pas de besoin d'agrégation SQL.
  const numericByQid = useMemo(() => {
    const map: Record<string, number[]> = {};
    for (const q of questions) {
      const k = (q.kind || q.type) as string;
      if (k === "rating" || k === "nps") map[q.id] = [];
    }
    for (const r of responses) {
      for (const it of r.items) {
        const bucket = map[it.questionId];
        if (bucket && typeof it.value === "number") bucket.push(it.value);
      }
    }
    return map;
  }, [responses, questions]);

  // Récupérer la comparaison simulation si le poll est terminé
  const comparison = useMemo(() => {
    if (poll?.status === "closed") {
      return getComparisonByPollId(poll.id);
    }
    return null;
  }, [poll]);

  const handleAnonymizeResponses = async () => {
    if (!poll) return;

    try {
      // Côté serveur via RPC SECURITY DEFINER (gate ownership : creator_id
      // ou guest_fingerprint matchant). On passe le device id pour le cas
      // guest — il sera ignoré côté serveur si l'utilisateur est authentifié.
      const { anonymizedCount } = await VoteService.anonymizeForPoll(
        poll.id,
        getDeviceId(),
      );

      if (anonymizedCount > 0) {
        const rows = await VoteService.listByPollIdAndType(poll.id, "form");
        const formResponses = rows.map(voteRowToFormResponse);
        const res = aggregateFormResults(poll.id, formResponses, questions);
        setCountsByQuestion(res.countsByQuestion);
        setTextAnswers(res.textAnswers);
        setDateResults(res.dateResults || {});
        setTotal(res.totalResponses);
        setResponses(formResponses);

        toast({
          title: t("forms.results.toast.anonymizedTitle"),
          description: t("forms.results.toast.anonymizedDesc", {
            count: anonymizedCount,
          }),
        });
      } else {
        toast({
          title: t("forms.results.toast.noNominativeTitle"),
          description: t("forms.results.toast.noNominativeDesc"),
        });
      }
    } catch (error) {
      toast({
        title: t("forms.results.toast.anonymizeErrorTitle"),
        description: t("forms.results.toast.anonymizeErrorDesc"),
        variant: "destructive",
      });
    }
  };

  // --- Export résultats (Phase 4 prod-readiness) ---
  // Réutilise les formatters existants (packages/core/src/lib/exports.ts) en
  // leur passant les réponses déjà chargées via VoteService (Supabase) — les
  // fonctions lisaient sinon localStorage (vide en prod).
  const exportPoll = useMemo(
    () => ({
      ...(poll as any),
      type: "form",
      slug: (poll as any)?.slug || poll?.id,
      created_at: (poll as any)?.created_at,
      poll_data_json: {
        questions,
        conditionalRules: (poll as any)?.conditionalRules || [],
      },
    }),
    [poll, questions],
  );

  const handleExport = async (fmt: "csv" | "pdf" | "json" | "markdown") => {
    setExportOpen(false);
    try {
      const {
        exportFormUnifiedPollToCSV,
        exportFormUnifiedPollToPDF,
        exportFormUnifiedPollToJSON,
        exportFormUnifiedPollToMarkdown,
      } = await import("@core/lib/exports");
      if (fmt === "csv")
        exportFormUnifiedPollToCSV(exportPoll as any, uniqueResponses);
      else if (fmt === "pdf")
        exportFormUnifiedPollToPDF(exportPoll as any, uniqueResponses);
      else if (fmt === "markdown")
        exportFormUnifiedPollToMarkdown(exportPoll as any, uniqueResponses);
      else exportFormUnifiedPollToJSON(exportPoll as any, uniqueResponses);
    } catch (e) {
      toast({
        title: t("forms.results.export.errorTitle"),
        description:
          e instanceof Error ? e.message : t("forms.results.export.errorDesc"),
        variant: "destructive",
      });
    }
  };

  // --- Lien public partageable (Phase 5) ---
  // La route /poll/:slug/results est servie publiquement quand la visibilité
  // est "public" (useResultsAccess). Le créateur peut copier ce lien.
  const isPublicResults =
    ((poll as any)?.resultsVisibility ??
      (poll as any)?.settings?.resultsVisibility) === "public";

  const handleCopyPublicLink = async () => {
    try {
      const url = `${window.location.origin}/poll/${(poll as any)?.slug || poll?.id}/results`;
      await navigator.clipboard.writeText(url);
      toast({ title: t("forms.results.publicLink.copied") });
    } catch {
      toast({
        title: t("forms.results.publicLink.copyError"),
        variant: "destructive",
      });
    }
  };

  // --- Code d'intégration iframe (Phase 6) ---
  // Le formulaire est par nature remplissable par quiconque a le lien ; le
  // snippet pointe vers la route /embed/:slug (vue nue, framing autorisé).
  const handleCopyEmbed = async () => {
    try {
      const slugOrId = (poll as any)?.slug || poll?.id;
      const src = `${window.location.origin}/embed/${slugOrId}`;
      const snippet = `<iframe src="${src}" width="100%" height="600" style="border:0" title="${t("forms.results.embed.iframeTitle")}"></iframe>`;
      await navigator.clipboard.writeText(snippet);
      toast({ title: t("forms.results.embed.copied") });
    } catch {
      toast({
        title: t("forms.results.embed.copyError"),
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pt-20">
        <ResultsLoading label={t("forms.results.loading")} />
      </div>
    );
  }

  if (error || !poll) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pt-20">
        <div className="text-center">
          <p className="text-gray-600 dark:text-gray-400">
            {error ? t("forms.results.loadError") : t("forms.results.notFound")}
          </p>
        </div>
      </div>
    );
  }

  if (!poll || poll.type !== "form") {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pt-20">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <ResultsEmpty message={<>{t("forms.results.pollNotFound")}</>} />
        </div>
      </div>
    );
  }

  // Vérifier l'accès aux résultats
  if (!accessStatus.allowed) {
    const errorAccess = accessStatus as {
      allowed: false;
      reason: "not-creator" | "not-voted" | "restricted";
      message: string;
    };
    return (
      <ResultsAccessDenied
        message={errorAccess.message}
        pollSlug={poll?.slug || idOrSlug}
        showVoteButton={errorAccess.reason === "not-voted"}
      />
    );
  }

  const totalRespondents = uniqueResponses.length;

  const getAccuracyColor = (score: number) => {
    if (score >= 80) return "text-green-600 bg-green-50 border-green-200";
    if (score >= 60) return "text-orange-600 bg-orange-50 border-orange-200";
    return "text-red-600 bg-red-50 border-red-200";
  };

  const getAccuracyIcon = (score: number) => {
    if (score >= 80) return <Target className="w-5 h-5" />;
    if (score >= 60) return <TrendingUp className="w-5 h-5" />;
    return <AlertCircle className="w-5 h-5" />;
  };

  return (
    <ResultsLayout
      data-testid="form-poll-results"
      title={t("forms.results.title", { title: poll.title })}
      subtitle={
        <>
          {t("forms.results.participantCount", { count: totalRespondents })}
          {" • "}
          {t("forms.results.questionCount", { count: questions.length })}
        </>
      }
      actions={
        <div className="w-full flex items-center gap-2 justify-end">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => navigate("/form/dashboard")}
            className="px-3 py-1.5 text-xs"
            data-testid="form-dashboard"
          >
            {t("nav.dashboard")}
          </Button>
          {isCreator && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={handleAnonymizeResponses}
              className="px-3 py-1.5 text-xs"
              data-testid="anonymize-form-responses-button"
            >
              {t("forms.results.anonymizeButton")}
            </Button>
          )}
          {isPublicResults && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={handleCopyPublicLink}
              className="px-3 py-1.5 text-xs"
              data-testid="copy-public-link-button"
            >
              {t("forms.results.publicLink.button")}
            </Button>
          )}
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={handleCopyEmbed}
            className="px-3 py-1.5 text-xs"
            data-testid="copy-embed-button"
          >
            {t("forms.results.embed.button")}
          </Button>
          {isCreator && uniqueResponses.length > 0 && (
            <div className="relative">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setExportOpen((o) => !o)}
                className="px-3 py-1.5 text-xs"
                aria-haspopup="menu"
                aria-expanded={exportOpen}
                data-testid="export-results-button"
              >
                {t("forms.results.export.button")}
              </Button>
              {exportOpen && (
                <div
                  className="absolute right-0 mt-1 z-20 bg-white border border-gray-200 rounded-md shadow-lg py-1 min-w-[150px]"
                  role="menu"
                >
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => handleExport("csv")}
                    className="block w-full text-left px-3 py-1.5 text-xs hover:bg-gray-100"
                    data-testid="export-csv"
                  >
                    {t("forms.results.export.csv")}
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => handleExport("pdf")}
                    className="block w-full text-left px-3 py-1.5 text-xs hover:bg-gray-100"
                    data-testid="export-pdf"
                  >
                    {t("forms.results.export.pdf")}
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => handleExport("json")}
                    className="block w-full text-left px-3 py-1.5 text-xs hover:bg-gray-100"
                    data-testid="export-json"
                  >
                    {t("forms.results.export.json")}
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => handleExport("markdown")}
                    className="block w-full text-left px-3 py-1.5 text-xs hover:bg-gray-100"
                    data-testid="export-markdown"
                  >
                    {t("forms.results.export.markdown")}
                  </button>
                </div>
              )}
            </div>
          )}
          <PollActions
            poll={adaptedPoll as any}
            showVoteButton
            onAfterDelete={() => navigate("/dashboard")}
          />
        </div>
      }
      onClose={() => navigate("/dashboard")}
      kpis={[
        { label: t("forms.results.kpi.participants"), value: totalRespondents },
        { label: t("forms.results.kpi.questions"), value: questions.length },
        {
          label: t("forms.results.kpi.choiceQuestions"),
          value: Object.keys(countsByQuestion).length,
        },
      ]}
    >
      <p className="sr-only" aria-live="polite">
        {t("forms.results.uniqueResponseCount", { count: totalRespondents })}
        {". "}
        {t("forms.results.questionCount", { count: questions.length })}
        {"."}
      </p>
      {/* Empty states inside content */}
      {questions.length === 0 ? (
        <ResultsEmpty message={<>{t("forms.results.noQuestions")}</>} />
      ) : totalRespondents === 0 ? (
        <ResultsEmpty message={<>{t("forms.results.noResponsesYet")}</>} />
      ) : (
        <div className="space-y-6">
          {/* Comparaison Simulation vs Réalité */}
          {comparison && (
            <div
              className={`rounded-lg border-2 p-4 sm:p-6 ${getAccuracyColor(comparison.accuracy.overall)}`}
              data-testid="simulation-comparison"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  {getAccuracyIcon(comparison.accuracy.overall)}
                  <div>
                    <h3 className="font-semibold text-lg">
                      {t("forms.results.simulation.title")}
                    </h3>
                    <p className="text-sm opacity-80">
                      {t("forms.results.simulation.subtitle")}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl sm:text-3xl font-bold">
                    {comparison.accuracy.overall}%
                  </div>
                  <div className="text-xs opacity-80">
                    {t("forms.results.simulation.overallAccuracy")}
                  </div>
                </div>
              </div>

              <Button
                type="button"
                size="sm"
                variant="link"
                onClick={() => setShowComparisonDetails(!showComparisonDetails)}
                className="text-sm font-medium mb-3"
                data-testid="formpollresults-button"
              >
                {showComparisonDetails
                  ? t("forms.results.simulation.hideDetails")
                  : t("forms.results.simulation.showDetails")}
              </Button>

              {showComparisonDetails && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4 pt-4 border-t border-current/20">
                  {/* Taux de complétion */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Target className="w-4 h-4" />
                      <span className="font-medium text-sm">
                        {t("forms.results.simulation.completionRate")}
                      </span>
                    </div>
                    <div className="text-xs space-y-1">
                      <div>
                        {t("forms.results.simulation.predicted")}:{" "}
                        <span className="font-semibold">
                          {Math.round(
                            comparison.predicted.avgCompletionRate * 100,
                          )}
                          %
                        </span>
                      </div>
                      <div>
                        {t("forms.results.simulation.actual")}:{" "}
                        <span className="font-semibold">
                          {Math.round(
                            comparison.actual.avgCompletionRate * 100,
                          )}
                          %
                        </span>
                      </div>
                      <div className="pt-1 border-t border-current/20">
                        {t("forms.results.simulation.accuracy")}:{" "}
                        <span className="font-bold">
                          {comparison.accuracy.completionRate}%
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Temps moyen */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      <span className="font-medium text-sm">
                        {t("forms.results.simulation.avgTime")}
                      </span>
                    </div>
                    <div className="text-xs space-y-1">
                      <div>
                        {t("forms.results.simulation.predicted")}:{" "}
                        <span className="font-semibold">
                          {Math.round(comparison.predicted.avgTotalTime)}s
                        </span>
                      </div>
                      <div>
                        {t("forms.results.simulation.actual")}:{" "}
                        <span className="font-semibold">
                          {Math.round(comparison.actual.avgTotalTime)}s
                        </span>
                      </div>
                      <div className="pt-1 border-t border-current/20">
                        {t("forms.results.simulation.accuracy")}:{" "}
                        <span className="font-bold">
                          {comparison.accuracy.totalTime}%
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Taux d'abandon */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      <span className="font-medium text-sm">
                        {t("forms.results.simulation.dropoffRate")}
                      </span>
                    </div>
                    <div className="text-xs space-y-1">
                      <div>
                        {t("forms.results.simulation.predicted")}:{" "}
                        <span className="font-semibold">
                          {Math.round(comparison.predicted.dropoffRate * 100)}%
                        </span>
                      </div>
                      <div>
                        {t("forms.results.simulation.actual")}:{" "}
                        <span className="font-semibold">
                          {Math.round(comparison.actual.dropoffRate * 100)}%
                        </span>
                      </div>
                      <div className="pt-1 border-t border-current/20">
                        {t("forms.results.simulation.accuracy")}:{" "}
                        <span className="font-bold">
                          {comparison.accuracy.dropoffRate}%
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Analytics IA Panel */}
          {totalRespondents > 0 ? (
            <div className="bg-white dark:bg-gray-800 rounded-md border dark:border-gray-700 p-4 sm:p-6">
              <PollAnalyticsPanel
                pollId={poll.id}
                pollTitle={poll.title}
                productType="form"
                questionTypes={questions.map((q: FormQuestionShape) => q.type)}
              />
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-md border dark:border-gray-700 p-4 sm:p-6">
              <div className="flex items-center gap-2">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {t("forms.results.analyticsAI")}
                </h2>
              </div>
            </div>
          )}

          {questions.map((q: FormQuestionShape) => {
            const qid: string = q.id;
            const kind: string = q.kind || q.type || "single";
            const stats = computed[qid];
            return (
              <div
                key={qid}
                className="bg-white dark:bg-gray-800 rounded-md border dark:border-gray-700 p-4"
                data-testid={`form-question-result-${qid}`}
              >
                <div className="mb-3">
                  <div className="font-medium dark:text-gray-100">
                    {q.title || t("forms.results.untitledQuestion")}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {kind === "text" || kind === "long-text"
                      ? t("forms.questionKind.freeText")
                      : kind === "single"
                        ? t("forms.questionKind.single")
                        : kind === "multiple"
                          ? t("forms.questionKind.multiple")
                          : kind === "matrix"
                            ? t("forms.questionKind.matrix")
                            : kind === "date"
                              ? t("forms.questionKind.date")
                              : kind === "rating"
                                ? t("forms.questionKind.rating")
                                : kind === "nps"
                                  ? t("forms.questionKind.nps")
                                  : t("forms.questionKind.generic")}
                  </div>
                </div>

                {kind === "date" && (
                  <div className="text-gray-500 p-4">
                    Date results view migrated to date-polls app
                  </div>
                )}

                {kind !== "text" &&
                  kind !== "long-text" &&
                  kind !== "matrix" &&
                  kind !== "date" && (
                    <div
                      className="space-y-2"
                      data-testid="question-options-results"
                    >
                      {(q.options || []).map((opt: FormQuestionOption) => {
                        const count = stats?.counts?.[opt.id] || 0;
                        const pct = totalRespondents
                          ? Math.round((count / totalRespondents) * 100)
                          : 0;
                        return (
                          <div key={opt.id} className="flex items-center gap-3">
                            <div className="w-48 text-sm text-gray-700 dark:text-gray-300">
                              {opt.label || t("forms.results.option")}
                            </div>
                            <div className="flex-1 h-2 bg-gray-100 dark:bg-gray-700 rounded">
                              <div
                                className="h-2 bg-blue-500 rounded"
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                            <div className="w-24 text-right text-sm text-gray-700 dark:text-gray-300">
                              {count} ({pct}%)
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                {kind === "matrix" && (
                  <div className="overflow-x-auto" data-testid="matrix-results">
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                      {t("forms.results.matrixResponses", {
                        count: totalRespondents,
                      })}
                    </div>
                    {!q.matrixRows || !q.matrixColumns ? (
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {t("forms.results.invalidMatrix")}
                      </div>
                    ) : (
                      <table className="w-full border-collapse text-sm">
                        <thead>
                          <tr>
                            <th className="border dark:border-gray-600 p-2 bg-gray-50 dark:bg-gray-700 text-left"></th>
                            {(q.matrixColumns || []).map(
                              (col: FormQuestionOption) => (
                                <th
                                  key={col.id}
                                  className="border dark:border-gray-600 p-2 bg-gray-50 dark:bg-gray-700 text-center font-medium dark:text-gray-200"
                                >
                                  {col.label}
                                </th>
                              ),
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {(q.matrixRows || []).map(
                            (row: FormQuestionOption) => (
                              <tr key={row.id}>
                                <td className="border dark:border-gray-600 p-2 font-medium bg-gray-50 dark:bg-gray-700 dark:text-gray-200">
                                  {row.label}
                                </td>
                                {(q.matrixColumns || []).map(
                                  (col: FormQuestionOption) => {
                                    const rowColKey = `${row.id}_${col.id}`;
                                    const count =
                                      stats?.counts?.[rowColKey] || 0;
                                    const pct = totalRespondents
                                      ? Math.round(
                                          (count / totalRespondents) * 100,
                                        )
                                      : 0;
                                    return (
                                      <td
                                        key={col.id}
                                        className="border dark:border-gray-600 p-2 text-center"
                                      >
                                        <div className="text-gray-700 dark:text-gray-300">
                                          {count} ({pct}%)
                                        </div>
                                      </td>
                                    );
                                  },
                                )}
                              </tr>
                            ),
                          )}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}

                {(kind === "text" || kind === "long-text") && (
                  <div className="space-y-2" data-testid="text-answers">
                    {(() => {
                      const all = textAnswers[qid] || [];
                      const isExpanded = !!expandedText[qid];
                      const list = isExpanded ? all : stats?.samples || [];
                      return list.length ? (
                        <>
                          {list.map((txt: string, i: number) => (
                            <div
                              key={i}
                              className="text-sm text-gray-800 dark:text-gray-200 border dark:border-gray-600 rounded px-3 py-2 bg-gray-50 dark:bg-gray-700"
                            >
                              {txt}
                            </div>
                          ))}
                          {all.length > (stats?.samples?.length || 0) && (
                            <div>
                              <Button
                                type="button"
                                size="sm"
                                variant="link"
                                className="mt-2 text-sm text-blue-600 dark:text-blue-400"
                                aria-expanded={isExpanded}
                                aria-controls={`text-q-${qid}`}
                                onClick={() =>
                                  setExpandedText((prev) => ({
                                    ...prev,
                                    [qid]: !isExpanded,
                                  }))
                                }
                                data-testid="expand-text-answers-button"
                              >
                                {isExpanded
                                  ? t("forms.results.showLess")
                                  : t("forms.results.showAll", {
                                      count: all.length,
                                    })}
                              </Button>
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {t("forms.results.noTextAnswers")}
                        </div>
                      );
                    })()}
                    <div
                      id={`text-q-${qid}`}
                      className="sr-only"
                      aria-live="polite"
                    >
                      {t("forms.results.srTextAnswers", {
                        count: textAnswers[qid]?.length || 0,
                      })}
                    </div>
                  </div>
                )}

                {kind === "rating" && (
                  <div className="space-y-4" data-testid="rating-results">
                    {(() => {
                      const ratingValues = numericByQid[qid] || [];

                      if (ratingValues.length === 0) {
                        return (
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {t("forms.results.noResponsesYetShort")}
                          </div>
                        );
                      }

                      const scale = q.ratingScale || 5;
                      const avg =
                        ratingValues.reduce((a, b) => a + b, 0) /
                        ratingValues.length;
                      const distribution = Array.from(
                        { length: scale },
                        (_, i) => {
                          const value = i + 1;
                          const count = ratingValues.filter(
                            (v) => v === value,
                          ).length;
                          const percent = Math.round(
                            (count / ratingValues.length) * 100,
                          );
                          return { value, count, percent };
                        },
                      );

                      return (
                        <>
                          <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                            <div className="text-center">
                              <div className="text-2xl sm:text-3xl font-bold text-blue-600 dark:text-blue-400">
                                {avg.toFixed(1)} / {scale}
                              </div>
                              <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                {t("forms.results.rating.average", {
                                  count: ratingValues.length,
                                })}
                              </div>
                            </div>
                          </div>
                          <div className="space-y-2">
                            {distribution.map((d) => (
                              <div
                                key={d.value}
                                className="flex items-center gap-3"
                              >
                                <span className="w-8 text-sm font-medium text-gray-700 dark:text-gray-300">
                                  {d.value}
                                </span>
                                <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-6">
                                  <div
                                    className="bg-blue-500 h-6 rounded-full flex items-center justify-end px-2 transition-all"
                                    style={{ width: `${d.percent}%` }}
                                  >
                                    {d.percent > 10 && (
                                      <span className="text-xs font-semibold text-white">
                                        {d.percent}%
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <span className="w-16 text-sm text-gray-600 dark:text-gray-400">
                                  {t("forms.results.rating.responsesCount", {
                                    count: d.count,
                                  })}
                                </span>
                              </div>
                            ))}
                          </div>
                        </>
                      );
                    })()}
                  </div>
                )}

                {kind === "nps" && (
                  <div data-testid="nps-results">
                    {(() => {
                      const npsValues = numericByQid[qid] || [];

                      if (npsValues.length === 0) {
                        return (
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {t("forms.results.noResponsesYetShort")}
                          </div>
                        );
                      }

                      return <NPSResults responses={npsValues} />;
                    })()}
                  </div>
                )}
              </div>
            );
          })}
          {/* Votes par participant */}
          <div
            className="bg-white dark:bg-gray-800 rounded-md border dark:border-gray-700 p-4"
            data-testid="individual-responses"
          >
            <div className="mb-3">
              <div className="font-medium dark:text-gray-100">
                {t("forms.results.byParticipant.title")}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {t("forms.results.byParticipant.subtitle")}
              </div>
            </div>
            <div className="divide-y dark:divide-gray-700">
              {uniqueResponses.map((r) => (
                <div
                  key={getRespondentId(r)}
                  className="py-3"
                  data-testid="respondent-response"
                >
                  <div className="flex items-center justify-between">
                    <div className="font-medium dark:text-gray-200">
                      {r.respondentName && r.respondentName.trim()
                        ? r.respondentName.trim()
                        : t("common.anonymous")}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {new Intl.DateTimeFormat(i18n.language || "fr", {
                        year: "numeric",
                        month: "2-digit",
                        day: "2-digit",
                        hour: "2-digit",
                        minute: "2-digit",
                        second: "2-digit",
                      }).format(new Date(r.created_at))}
                    </div>
                  </div>
                  <div className="mt-2 grid gap-2 sm:grid-cols-2">
                    {r.items.map((it) => {
                      const q = questions.find(
                        (qq: FormQuestionShape) => qq.id === it.questionId,
                      );
                      if (!q) return null;
                      const kind = q.kind || q.type || "single";
                      let answerDisplay: string = "";
                      if (kind === "text" || kind === "long-text") {
                        answerDisplay =
                          typeof it.value === "string" ? it.value : "";
                      } else if (kind === "date") {
                        // Afficher les votes sur dates
                        const dateValue = it.value as
                          | import("@core/lib/pollStorage").DateVoteValue
                          | undefined;
                        if (dateValue && Array.isArray(dateValue)) {
                          const voteStrings = dateValue.map((vote) => {
                            // Parser la date YYYY-MM-DD correctement
                            const [year, month, day] = vote.date
                              .split("-")
                              .map(Number);
                            const dateObj = new Date(year, month - 1, day);
                            const dateStr = new Intl.DateTimeFormat(
                              i18n.language || "fr",
                              {
                                day: "numeric",
                                month: "short",
                              },
                            ).format(dateObj);
                            const timeStr =
                              vote.timeSlots.length > 0
                                ? vote.timeSlots
                                    .map(
                                      (slot) =>
                                        `${slot.hour.toString().padStart(2, "0")}:${slot.minute.toString().padStart(2, "0")}`,
                                    )
                                    .join(", ")
                                : "";
                            const voteEmoji =
                              vote.vote === "yes"
                                ? "✓"
                                : vote.vote === "no"
                                  ? "✗"
                                  : "?";
                            return `${voteEmoji} ${dateStr}${timeStr ? ` (${timeStr})` : ""}`;
                          });
                          answerDisplay = voteStrings.join(" • ");
                        } else {
                          answerDisplay = "—";
                        }
                      } else if (kind === "matrix") {
                        // Pour matrices, afficher un résumé
                        const matrixVal = it.value as Record<
                          string,
                          string | string[]
                        >;
                        if (
                          matrixVal &&
                          typeof matrixVal === "object" &&
                          !Array.isArray(matrixVal) &&
                          q.matrixRows &&
                          q.matrixColumns
                        ) {
                          const rowLabels = (q.matrixRows || [])
                            .map((row: FormQuestionOption) => {
                              const rowAnswer = matrixVal[row?.id];
                              if (!rowAnswer || !row) return null;
                              const colIds = Array.isArray(rowAnswer)
                                ? rowAnswer
                                : [rowAnswer];
                              const colLabels = colIds.map((cid: string) => {
                                const col = (q.matrixColumns || []).find(
                                  (c: FormQuestionOption) => c?.id === cid,
                                );
                                return col ? col.label : cid;
                              });
                              return `${row.label}: ${colLabels.join(", ")}`;
                            })
                            .filter(Boolean);
                          answerDisplay = rowLabels.join(" • ");
                        } else {
                          answerDisplay = "—";
                        }
                      } else if (kind === "single") {
                        const opt = (q?.options || []).find(
                          (o: FormQuestionOption) => o.id === it.value,
                        );
                        answerDisplay = opt
                          ? opt.label
                          : String(it.value || "");
                      } else if (kind === "rating") {
                        // Note numérique : "4/5" (scale par défaut 5).
                        const scale = (q as any).ratingScale || 5;
                        answerDisplay =
                          it.value != null && it.value !== ""
                            ? `${it.value}/${scale}`
                            : "—";
                      } else if (kind === "nps") {
                        // Score 0-10 + label promoteur/passif/détracteur.
                        const score = Number(it.value);
                        if (Number.isFinite(score)) {
                          const cat =
                            score >= 9
                              ? t("nps.category.promoter")
                              : score >= 7
                                ? t("nps.category.passive")
                                : t("nps.category.detractor");
                          answerDisplay = `${score}/10 (${cat})`;
                        } else {
                          answerDisplay = "—";
                        }
                      } else {
                        const ids = Array.isArray(it.value)
                          ? (it.value as string[])
                          : [];
                        const labels = ids.map((id: string) => {
                          const o = (q?.options || []).find(
                            (oo: FormQuestionOption) => oo.id === id,
                          );
                          return o ? o.label : id;
                        });
                        answerDisplay = labels.join(", ");
                      }
                      return (
                        <div key={it.questionId} className="text-sm">
                          <div className="text-gray-600 dark:text-gray-400">
                            {q.title || t("forms.questionKind.generic")}
                          </div>
                          <div className="text-gray-900 dark:text-gray-100">
                            {answerDisplay || "—"}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </ResultsLayout>
  );
}
